import React from 'react';

type MarketingLandingProps = {
  canSignIn: boolean;
  error: string | null;
  onDismissError: () => void;
  onSignIn: () => void;
};

const capabilities = [
  {
    number: '01',
    title: 'Agent workflows',
    body: 'Give autonomous agents a shared workspace to plan, execute and hand off operational work.',
    tone: 'green'
  },
  {
    number: '02',
    title: 'Governed execution',
    body: 'Keep approvals, identities and policy boundaries connected to every consequential action.',
    tone: 'blue'
  },
  {
    number: '03',
    title: 'Operational visibility',
    body: 'Follow conversations, jobs, logs and outcomes from one control plane instead of scattered tools.',
    tone: 'violet'
  },
  {
    number: '04',
    title: 'Infrastructure control',
    body: 'Coordinate executors, terminals and environments while retaining a clear operational trail.',
    tone: 'gold'
  }
] as const;

const workflow = [
  ['Connect', 'Bring projects, environments and agent identities into one governed workspace.'],
  ['Orchestrate', 'Turn intent into repeatable workflows with explicit context and control points.'],
  ['Observe', 'See progress, decisions and runtime output while work is happening.'],
  ['Improve', 'Use the recorded outcome to refine the next run instead of starting from scratch.']
] as const;

function ArrowIcon() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true">
      <path d="M4 10h11M11 6l4 4-4 4" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true">
      <path d="m4.5 10.5 3.2 3.2 7.8-8" />
    </svg>
  );
}

export default function MarketingLanding({
  canSignIn,
  error,
  onDismissError,
  onSignIn
}: MarketingLandingProps) {
  return (
    <div className="mc-marketing">
      <a className="mc-skip-link" href="#mc-main">Skip to content</a>

      <header className="mc-header">
        <a className="mc-brand" href="https://yaaf.de" aria-label="YAAF home">
          <img src="/brand/yaaf-wordmark.svg" alt="YAAF" />
          <span>Mission Control</span>
        </a>
        <nav aria-label="Mission Control navigation">
          <a href="#capabilities">Capabilities</a>
          <a href="#operations">How it works</a>
          <a href="#governance">Governance</a>
        </nav>
        <button className="mc-header-sign-in" type="button" onClick={onSignIn} disabled={!canSignIn}>
          Sign in <ArrowIcon />
        </button>
      </header>

      <main id="mc-main">
        {error && (
          <div className="mc-error" role="alert">
            <span>{error}</span>
            <button type="button" onClick={onDismissError} aria-label="Dismiss error">×</button>
          </div>
        )}

        <section className="mc-hero" aria-labelledby="mc-hero-title">
          <div className="mc-hero-copy">
            <div className="mc-eyebrow"><span /> YAAF / Agent operations platform</div>
            <h1 id="mc-hero-title">
              One launch.<br />
              <span>The whole mission.</span><br />
              Under control.
            </h1>
            <p>
              Coordinate autonomous agents from the first task to a verified outcome—across
              software delivery, monitoring, security and infrastructure.
            </p>
            <div className="mc-hero-actions">
              <button className="mc-primary-action" type="button" onClick={onSignIn} disabled={!canSignIn}>
                {canSignIn ? 'Open Mission Control' : 'Preparing sign in…'} <ArrowIcon />
              </button>
              <a className="mc-secondary-action" href="mailto:support@yaaf.de?subject=YAAF%20Mission%20Control%20demo">
                Book a tailored demo
              </a>
            </div>
            <div className="mc-principles" aria-label="Platform principles">
              <span><CheckIcon /> Visible by default</span>
              <span><CheckIcon /> Human guardrails</span>
              <span><CheckIcon /> One control plane</span>
            </div>
          </div>

          <div className="mc-console-wrap" aria-label="Mission Control workflow preview">
            <div className="mc-console-glow" />
            <div className="mc-console">
              <div className="mc-console-bar">
                <div className="mc-console-dots"><i /><i /><i /></div>
                <span>MISSION CONTROL / LIVE OPERATIONS</span>
                <b><i /> SYSTEM READY</b>
              </div>
              <div className="mc-console-body">
                <aside className="mc-console-nav" aria-hidden="true">
                  <div className="mc-mini-logo">MC</div>
                  <i className="active" /><i /><i /><i /><i />
                </aside>
                <div className="mc-console-main">
                  <div className="mc-console-heading">
                    <div><span>WORKSPACE</span><strong>Release operations</strong></div>
                    <button type="button" tabIndex={-1}>+ New workflow</button>
                  </div>
                  <div className="mc-metric-grid">
                    <div><span>ACTIVE WORK</span><strong>3</strong><small>agents coordinating</small></div>
                    <div><span>CONTROL GATES</span><strong>1</strong><small>approval requested</small></div>
                    <div><span>SYSTEMS</span><strong>8</strong><small>connected targets</small></div>
                  </div>
                  <div className="mc-workflow-card">
                    <div className="mc-workflow-title">
                      <div><i /><span><b>Production release</b><small>Workflow / deploy-202</small></span></div>
                      <em>IN PROGRESS</em>
                    </div>
                    <div className="mc-workflow-line">
                      <div className="done"><i>✓</i><span>Plan</span></div>
                      <b />
                      <div className="done"><i>✓</i><span>Verify</span></div>
                      <b className="active" />
                      <div className="current"><i>3</i><span>Approve</span></div>
                      <b />
                      <div><i>4</i><span>Deploy</span></div>
                    </div>
                    <div className="mc-approval">
                      <span>POLICY CHECK</span>
                      <strong>Production change is ready for approval</strong>
                      <small>All automated checks passed · evidence attached</small>
                      <button type="button" tabIndex={-1}>Review action</button>
                    </div>
                  </div>
                  <div className="mc-event-row">
                    <span>06:42:18</span><i /> <b>release-agent</b><p>Verification completed successfully</p><em>VERIFIED</em>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mc-capabilities" id="capabilities" aria-labelledby="mc-capabilities-title">
          <div className="mc-section-heading">
            <span>01 / THE CONTROL PLANE</span>
            <h2 id="mc-capabilities-title">One place for the work<br />between your systems.</h2>
            <p>Purpose-built for operational agents that need real access, real context and real accountability.</p>
          </div>
          <div className="mc-capability-grid">
            {capabilities.map((capability) => (
              <article className={`mc-capability mc-capability--${capability.tone}`} key={capability.number}>
                <span>{capability.number}</span>
                <div className="mc-capability-icon" aria-hidden="true"><i /><i /><i /></div>
                <h3>{capability.title}</h3>
                <p>{capability.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mc-operations" id="operations" aria-labelledby="mc-operations-title">
          <div className="mc-section-heading mc-section-heading--light">
            <span>02 / FROM INTENT TO OUTCOME</span>
            <h2 id="mc-operations-title">Automation that stays<br />under control.</h2>
          </div>
          <div className="mc-operation-layout">
            <ol>
              {workflow.map(([title, body], index) => (
                <li key={title}>
                  <span>0{index + 1}</span>
                  <div><h3>{title}</h3><p>{body}</p></div>
                </li>
              ))}
            </ol>
            <div className="mc-operation-quote">
              <span>OPERATING PRINCIPLE</span>
              <blockquote>Autonomy should increase your leverage — not reduce your visibility.</blockquote>
              <p>Mission Control keeps execution, evidence and human decisions in the same operational record.</p>
            </div>
          </div>
        </section>

        <section className="mc-governance" id="governance" aria-labelledby="mc-governance-title">
          <div className="mc-governance-copy">
            <span>03 / GOVERNED BY DESIGN</span>
            <h2 id="mc-governance-title">Trust is part of<br />the workflow.</h2>
            <p>
              Move faster without turning your production systems into a black box. Mission Control makes
              identity, authorization and operational evidence first-class parts of agent execution.
            </p>
            <a href="mailto:support@yaaf.de?subject=YAAF%20Mission%20Control%20security">Discuss your requirements <ArrowIcon /></a>
          </div>
          <div className="mc-governance-grid">
            <div><b>01</b><h3>Verified identity</h3><p>Connect work to the user, agent and workload responsible for it.</p></div>
            <div><b>02</b><h3>Scoped access</h3><p>Keep projects, environments and privileged actions behind explicit boundaries.</p></div>
            <div><b>03</b><h3>Approval gates</h3><p>Place human decisions exactly where operational risk requires them.</p></div>
            <div><b>04</b><h3>Traceable outcomes</h3><p>Retain the context, runtime output and result of every governed workflow.</p></div>
          </div>
        </section>

        <section className="mc-final-cta" aria-labelledby="mc-final-title">
          <span>READY WHEN YOU ARE</span>
          <h2 id="mc-final-title">Give your agents a<br />place to operate.</h2>
          <p>Bring autonomous work into one visible, governed control plane.</p>
          <div>
            <button className="mc-primary-action" type="button" onClick={onSignIn} disabled={!canSignIn}>
              Sign in to Mission Control <ArrowIcon />
            </button>
            <a className="mc-secondary-action" href="mailto:support@yaaf.de?subject=YAAF%20Mission%20Control%20demo">Talk to us</a>
          </div>
        </section>
      </main>

      <footer className="mc-footer">
        <a className="mc-brand" href="https://yaaf.de" aria-label="YAAF home">
          <img src="/brand/yaaf-wordmark.svg" alt="YAAF" />
          <span>Mission Control</span>
        </a>
        <p>Yet Another Automation Factory · Agent operations, under control.</p>
        <div><a href="https://yaaf.de">yaaf.de</a><a href="mailto:support@yaaf.de">Contact</a></div>
      </footer>
    </div>
  );
}
